import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@kss/api';

/**
 * Generic typeahead picker for FK selection. Loads a resource list once
 * on first focus, filters client-side, and calls `onChange` with the
 * full selected row (so the parent can read name / gstin / etc.).
 *
 * Designed for the sales composers — Customer, Product, etc.
 *
 * Notes on two subtle bugs this version fixes:
 *  - **Clipping inside scroll containers.** The product picker lives in a
 *    line-items table wrapped in `overflow-x-auto`, which also clips the
 *    Y axis, so an `absolute` dropdown got hidden. The dropdown now uses
 *    `position: fixed` anchored to the input's bounding rect, escaping all
 *    ancestor overflow/clip contexts.
 *  - **Stale list after an inline add.** `selectedRow` lets the parent
 *    pass a freshly-created row so the field shows its label even before a
 *    re-fetch; `reloadKey` forces a re-fetch so the new row appears in the
 *    dropdown list too.
 */

interface EntityPickerProps<T> {
  basePath: string;
  idKey: string;
  labelKey: string;
  secondaryKey?: string;
  value: string | number | null;
  onChange: (row: T | null) => void;
  placeholder?: string;
  /** Display fallback for a value not yet present in the fetched list. */
  selectedRow?: T | null;
  /** Bump to force a re-fetch (e.g. after inline-adding a row). */
  reloadKey?: number;
  /** Pre-built option rows. When provided, the picker uses these instead
   *  of fetching `basePath` — lets the parent merge multiple sources
   *  (e.g. Products + Variants + Bundles). */
  rows?: T[];
}

export function EntityPicker<T extends Record<string, unknown>>({
  basePath,
  idKey,
  labelKey,
  secondaryKey,
  value,
  onChange,
  placeholder,
  selectedRow,
  reloadKey,
  rows: providedRows,
}: EntityPickerProps<T>) {
  const [fetchedRows, setFetchedRows] = useState<T[]>([]);
  const rows = providedRows ?? fetchedRows;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [fetched, setFetched] = useState(false);
  const [rect, setRect] = useState<{ left: number; width: number; maxH: number; top?: number; bottom?: number } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Resolve the current value to a display label — prefer a row from the
  // fetched list, fall back to the externally-supplied selectedRow.
  const selected =
    rows.find((r) => String(r[idKey] ?? r.id) === String(value)) ??
    (selectedRow && String(selectedRow[idKey] ?? selectedRow.id) === String(value)
      ? selectedRow
      : undefined);

  // Re-fetch when reloadKey changes (after an inline add).
  useEffect(() => {
    if (reloadKey === undefined) return;
    setFetched(false);
    void ensureLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Keep the fixed-position dropdown anchored while open.
  useEffect(() => {
    if (!open) return;
    function reposition() {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Flip the menu UP when there isn't room below (otherwise it ran off the
      // bottom of the screen on low-positioned fields — #6/#8).
      const below = window.innerHeight - r.bottom;
      const above = r.top;
      const openUp = below < 220 && above > below;
      setRect({
        left: r.left,
        width: r.width,
        maxH: Math.max(140, Math.min(240, (openUp ? above : below) - 12)),
        ...(openUp ? { bottom: window.innerHeight - r.top + 2 } : { top: r.bottom + 2 }),
      });
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  async function ensureLoaded(force = false) {
    if (providedRows) return;            // parent supplies the option list
    if (fetched && !force) return;
    setLoading(true);
    try {
      const r = await apiClient.get(basePath);
      const d = r.data;
      const list = Array.isArray(d) ? d : (d?.items ?? d?.data ?? []);
      setFetchedRows(Array.isArray(list) ? list : []);
      setFetched(true);
    } catch {
      setFetchedRows([]);
    } finally {
      setLoading(false);
    }
  }

  const filtered = query.trim()
    ? rows.filter((r) => {
        const hay = `${String(r[labelKey] ?? '')} ${String(r[secondaryKey ?? ''] ?? '')} ${String(r[idKey] ?? '')}`.toLowerCase();
        return hay.includes(query.trim().toLowerCase());
      })
    : rows;

  return (
    <div ref={boxRef} className="relative">
      <input
        ref={inputRef}
        className="h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary"
        value={open ? query : (selected ? `${String(selected[labelKey] ?? '')}${secondaryKey && selected[secondaryKey] ? ` · ${String(selected[secondaryKey])}` : ''}` : '')}
        placeholder={placeholder}
        onFocus={() => {
          setOpen(true);
          void ensureLoaded();
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!open) setOpen(true);
        }}
      />
      {open && rect && (
        <div
          className="z-[1000] overflow-auto rounded-md border border-border bg-surface-card shadow-lg"
          style={{ position: 'fixed', left: rect.left, top: rect.top, bottom: rect.bottom, width: rect.width, maxHeight: rect.maxH }}
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-content-muted">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-content-muted">No matches</div>
          ) : (
            filtered.slice(0, 50).map((r) => (
              <button
                key={String(r[idKey] ?? r.id)}
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-surface-muted"
                onClick={() => {
                  onChange(r);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {String(r[labelKey] ?? '')}
                {secondaryKey && r[secondaryKey] ? (
                  <span className="text-content-muted"> · {String(r[secondaryKey])}</span>
                ) : null}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
